<template>
  <div class="page-container bg-surface-50 dark:bg-surface-950">
    <Navbar />

    <main class="page-content">
      <!-- Header with Back Button -->
      <div class="page-header animate-fade-up">
        <button
          @click="$router.back()"
          class="inline-flex items-center text-accent-500 hover:text-accent-400 mb-4 transition-all duration-200 hover:-translate-x-0.5 font-medium group"
        >
          <ArrowLeftIcon class="h-5 w-5 mr-2 transition-transform duration-200 group-hover:-translate-x-1" />
          Back to Dashboard
        </button>
        <div class="flex items-start justify-between">
          <div>
            <h1 class="text-display-sm text-surface-900 dark:text-surface-50 mb-2">
              UCO Collection History
            </h1>
            <p class="text-surface-500 dark:text-surface-400">
              Track and analyze used cooking oil collections from suppliers
            </p>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div class="animate-fade-up mb-8" style="animation-delay: 60ms">
        <BaseCard class="sticky top-4 z-10 border border-surface-200/60 dark:border-surface-700/60 backdrop-blur-sm bg-white/80 dark:bg-surface-900/80 rounded-2xl">
          <div class="flex items-center gap-3 mb-4">
            <div class="p-1.5 rounded-lg bg-accent-500/10">
              <AdjustmentsHorizontalIcon class="h-5 w-5 text-accent-500" />
            </div>
            <h3 class="text-title text-surface-900 dark:text-surface-50">Filters & Date Range</h3>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <DateRangePicker v-model="dateRange" :max="todayIso" />
            <div>
              <label class="label">Supplier Filter:</label>
              <VueMultiselect
                v-model="selectedSuppliers"
                :options="allSuppliers"
                :multiple="true"
                :close-on-select="false"
                placeholder="Select Suppliers"
                class="multiselect-tailwind"
              />
            </div>
          </div>
        </BaseCard>
      </div>

      <!-- Quick Stats -->
      <div class="animate-fade-up mb-8" style="animation-delay: 120ms">
        <QuickStats :stats="quickStats" />
      </div>

      <!-- Analytics Charts -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 animate-fade-up" style="animation-delay: 180ms">
        <BaseCard class="rounded-2xl border border-surface-200/60 dark:border-surface-700/60 bg-white dark:bg-surface-900/80 dark:backdrop-blur-sm">
          <template #header>
            <div class="flex items-center gap-3">
              <div class="p-1.5 rounded-lg bg-accent-500/10">
                <ChartBarIcon class="h-5 w-5 text-accent-500" />
              </div>
              <h3 class="text-title text-surface-900 dark:text-surface-50">
                Volume by Supplier
              </h3>
            </div>
          </template>
          <p class="text-sm text-surface-500 dark:text-surface-400 mb-4">
            UCO collection distribution across suppliers
          </p>
          <div class="h-96">
            <CollectionsBarChart
              :chart-data="chartData"
              :chart-options="chartOptions"
              :key="chartKey"
            />
          </div>
        </BaseCard>

        <BaseCard class="rounded-2xl border border-surface-200/60 dark:border-surface-700/60 bg-white dark:bg-surface-900/80 dark:backdrop-blur-sm">
          <template #header>
            <div class="flex items-center gap-3">
              <div class="p-1.5 rounded-lg bg-accent-500/10">
                <CalendarIcon class="h-5 w-5 text-accent-500" />
              </div>
              <h3 class="text-title text-surface-900 dark:text-surface-50">
                Monthly Collection Trend
              </h3>
            </div>
          </template>
          <p class="text-sm text-surface-500 dark:text-surface-400 mb-4">
            UCO collection pattern over time
          </p>
          <div class="h-96">
            <CollectionsBarChart
              :chart-data="chartData2"
              :chart-options="chartOptions"
              :key="chartKey"
            />
          </div>
        </BaseCard>
      </div>

      <!-- Collections Table -->
      <div class="animate-fade-up" style="animation-delay: 240ms">
        <BaseCard class="rounded-2xl border border-surface-200/60 dark:border-surface-700/60 bg-white dark:bg-surface-900/80 dark:backdrop-blur-sm">
          <template #header>
            <div class="flex items-center gap-3">
              <div class="p-1.5 rounded-lg bg-accent-500/10">
                <TableCellsIcon class="h-6 w-6 text-accent-500" />
              </div>
              <h3 class="text-title text-surface-900 dark:text-surface-50">
                Collection Details
              </h3>
            </div>
          </template>

          <DataTable
            v-if="transactions.length"
            :headers="headers"
            :items="transactions"
            :search-placeholder="`Search collections`"
            :rows-per-page="25"
            :export-filename="`uco-collections-${startDate}-${endDate}.csv`"
          >
            <template #item-DateTimeOfCollection="{ DateTimeOfCollection }">
              <span class="text-surface-600 dark:text-surface-300 font-mono text-sm">
                {{ formatDate(DateTimeOfCollection) }}
              </span>
            </template>

            <template #item-PurchaseOrderNumber="{ PurchaseOrderNumber }">
              <span class="text-surface-600 dark:text-surface-300 font-mono text-sm">
                {{ PurchaseOrderNumber }}
              </span>
            </template>

            <template #item-Quantity="{ Quantity }">
              <span class="font-semibold font-mono text-surface-900 dark:text-surface-50">
                {{ formatNumber(Quantity) }} L
              </span>
            </template>

            <template #item-SupplierName="{ SupplierName }">
              <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-accent-50 text-accent-700 dark:bg-accent-900/30 dark:text-accent-300 border border-accent-200/50 dark:border-accent-700/30">
                {{ SupplierName }}
              </span>
            </template>

            <template #item-LocationName="{ LocationName }">
              <div class="flex items-center gap-2">
                <MapPinIcon class="h-4 w-4 text-surface-400" />
                <span class="text-surface-600 dark:text-surface-300">
                  {{ LocationName }}
                </span>
              </div>
            </template>

            <template #empty>
              <EmptyState
                :icon="BeakerIcon"
                title="No collections found"
                message="There are no UCO collections for the selected period. Try adjusting your date range or filters."
                variant="info"
              />
            </template>
          </DataTable>
        </BaseCard>
      </div>
    </main>

    <Footer />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { authFetch } from '@/composables/useApi'

import Navbar from '@/components/Navbar.vue'
import Footer from '@/components/Footer.vue'
import BaseCard from '@/components/ui/BaseCard.vue'
import DateRangePicker from '@/components/dashboard/DateRangePicker.vue'
import CollectionsBarChart from '@/components/CollectionsBarChart.vue'
import DataTable from '@/components/ui/DataTable.vue'
import QuickStats from '@/components/ui/QuickStats.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import VueMultiselect from 'vue-multiselect'

import {
  ArrowLeftIcon,
  BeakerIcon,
  ChartBarIcon,
  CalendarIcon,
  TableCellsIcon,
  AdjustmentsHorizontalIcon,
  BuildingStorefrontIcon,
  MapPinIcon
} from '@heroicons/vue/24/outline'

const store = useStore()
const route = useRoute()

const group_id = route.params.group_id
const transactions = ref([])
const _now = new Date()
const todayIso = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, '0')}-${String(_now.getDate()).padStart(2, '0')}`
const dateRange = ref({
  startDate: new Date(new Date().getFullYear(), new Date().getMonth() - 6, 2)
    .toISOString()
    .substr(0, 10),
  endDate: todayIso
})
const chartKey = ref(0)

const startDate = computed(() => dateRange.value.startDate)
const endDate = computed(() => dateRange.value.endDate)

const selectedSuppliers = ref([])
const allSuppliers = ref([])

// Quick Stats
const quickStats = computed(() => {
  if (!transactions.value.length) return []

  const totalCollections = transactions.value.length
  const totalVolume = transactions.value.reduce((sum, t) => sum + t.Quantity, 0)
  const uniqueSuppliers = new Set(transactions.value.map(t => t.SupplierName)).size
  const uniqueLocations = new Set(transactions.value.map(t => t.LocationName)).size

  return [
    {
      label: 'Total Collections',
      value: totalCollections,
      icon: BeakerIcon,
      color: '#96c33d'
    },
    {
      label: 'Active Suppliers',
      value: uniqueSuppliers,
      icon: BuildingStorefrontIcon,
      color: '#8fb300'
    },
    {
      label: 'Total Volume',
      value: `${formatNumber(totalVolume)} L`,
      icon: ChartBarIcon,
      color: '#7a9900'
    },
    {
      label: 'Collection Points',
      value: uniqueLocations,
      icon: MapPinIcon,
      color: '#698000'
    }
  ]
})

const headers = [
  { value: 'DateTimeOfCollection', text: 'Date', sortable: true },
  { value: 'PurchaseOrderNumber', text: 'PO Number', sortable: true },
  { value: 'SupplierName', text: 'Supplier Name', sortable: true },
  { value: 'LocationName', text: 'Location Name', sortable: true },
  { value: 'Quantity', text: 'Volume (L)', sortable: true },
]

const formatDate = (isoDate) => {
  const date = new Date(isoDate)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatNumber = (num) => {
  return num?.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

const fetchTransactions = async () => {
  const apiUrl = store.state.apiUrl + 'getAllSupplierCollectionsByGroup'

  try {
    const response = await authFetch(
      `${apiUrl}/${group_id}?start_date=${startDate.value}&end_date=${endDate.value}`,
      {},
      store.state.user
    )

    if (response.ok) {
      const result = await response.json()
      let fetchedTransactions = result[1].collectionData

      const suppliersSet = new Set()
      fetchedTransactions.forEach((transaction) => {
        suppliersSet.add(transaction.SupplierName)
      })
      allSuppliers.value = Array.from(suppliersSet)

      if (selectedSuppliers.value.length > 0) {
        fetchedTransactions = fetchedTransactions.filter((transaction) =>
          selectedSuppliers.value.includes(transaction.SupplierName)
        )
      }

      transactions.value = fetchedTransactions
      updateCharts()
    } else {
      console.error('Error fetching collections')
    }
  } catch (error) {
    console.error('Error:', error)
  }
}

const chartData = ref({
  labels: [],
  datasets: [
    {
      label: 'Collection Volume (L)',
      backgroundColor: '#96c33d',
      data: [],
    },
  ],
})

const chartData2 = ref({
  labels: [],
  datasets: [
    {
      label: 'Monthly Collection Volume (L)',
      backgroundColor: '#8fb300',
      data: [],
    },
  ],
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: true, position: 'bottom' },
  },
  scales: {
    x: { beginAtZero: true },
    y: { beginAtZero: true },
  },
}

const updateCharts = () => {
  // Chart 1: Volume by Supplier
  const supplierMap = {}
  transactions.value.forEach((transaction) => {
    const supplier = transaction.SupplierName
    if (!supplierMap[supplier]) {
      supplierMap[supplier] = 0
    }
    supplierMap[supplier] += transaction.Quantity
  })

  chartData.value.labels = Object.keys(supplierMap)
  chartData.value.datasets[0].data = Object.values(supplierMap)

  // Chart 2: Monthly Volume
  const monthMap = {}
  transactions.value.forEach((transaction) => {
    const date = new Date(transaction.CollectionDate)
    const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    if (!monthMap[monthYear]) {
      monthMap[monthYear] = 0
    }
    monthMap[monthYear] += transaction.Quantity
  })

  const sortedMonths = Object.keys(monthMap).sort()
  chartData2.value.labels = sortedMonths.map((m) => {
    const [year, month] = m.split('-')
    return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][parseInt(month) - 1]} ${year}`
  })
  chartData2.value.datasets[0].data = sortedMonths.map((m) => monthMap[m])

  chartKey.value++
}

fetchTransactions()

watch([startDate, endDate, selectedSuppliers], () => {
  fetchTransactions()
})
</script>

<style>
@import "../../../node_modules/vue-multiselect/dist/vue-multiselect.css";

.multiselect-tailwind {
  @apply rounded-xl;
}

.multiselect-tailwind .multiselect__tags {
  @apply rounded-xl border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800;
}

.multiselect-tailwind .multiselect__input,
.multiselect-tailwind .multiselect__single {
  @apply text-surface-900 dark:text-surface-100 bg-transparent;
}

.multiselect-tailwind .multiselect__tag {
  @apply bg-accent-500 text-white rounded-lg;
}

.multiselect-tailwind .multiselect__content-wrapper {
  @apply border-surface-200 dark:border-surface-600 bg-white dark:bg-surface-800 rounded-xl mt-1;
}

.multiselect-tailwind .multiselect__option {
  @apply text-surface-900 dark:text-surface-100;
}

.multiselect-tailwind .multiselect__option--highlight {
  @apply bg-accent-500 text-white;
}
</style>

<style scoped>
/* Smooth transitions for interactive elements */
button {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
